import * as React from 'react'
import clx from 'classnames'

import * as dayjs from 'dayjs'
import { CheckIcon } from '@/app/features/icons/Check'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type Step = {
  id?: string | number
  progressAmount: number
  allAmount: number
  status?: 'completed' | 'active' | 'upcoming'
  children?: Step[]
}

type Props = {
  totalDrivers?: number
  settlementTotalDrivers?: number
  progressAmount?: number
  className?: string
  isSublist?: boolean
  finalizedAt?: string
  driversWithPrematureRead?: number
  selectedMonth?: dayjs.Dayjs
}

export const DashboardStepper = ({
  totalDrivers,
  settlementTotalDrivers,
  progressAmount,
  className,
  finalizedAt,
  driversWithPrematureRead,
  selectedMonth,
  isSublist = false,
}: Props) => {
  const { t } = useLocalize()

  const totalDriversCount = totalDrivers ?? 0
  const safeProgress = Math.min(
    Math.max(progressAmount ?? 0, 0),
    totalDriversCount,
  )
  const settlementTotalCount = settlementTotalDrivers ?? totalDriversCount
  const safeSettled = Math.min(
    Math.max(progressAmount ?? 0, 0),
    settlementTotalCount,
  )
  const isImportComplete =
    !!finalizedAt ||
    (totalDriversCount > 0 && safeProgress >= totalDriversCount)
  const isSettlementComplete = !!finalizedAt

  const steps = [
    {
      id: 1,
      progressAmount: safeProgress,
      totalDrivers: totalDriversCount,
      status: isImportComplete ? 'completed' : 'upcoming',
    },
    {
      id: 2,
      progressAmount: safeSettled,
      totalDrivers: settlementTotalCount,
      status: isSettlementComplete ? 'completed' : 'upcoming',
    },
  ]

  const nextMonthStart = (selectedMonth ?? dayjs())
    .add(1, 'month')
    .startOf('month')
  const readyToCountDate = nextMonthStart.format('D.MM.YY')
  return (
    <ol className={clx('tw-flex tw-flex-col tw-gap-4', className)}>
      {steps.map((step, index) => {
        const isLastStep = !isSublist && index === steps.length - 1
        const showLine = !isLastStep || (isLastStep && step.status === 'active')
        return (
          <React.Fragment key={step.id ?? index}>
            <li
              className={clx(
                'tw-relative tw-flex tw-gap-2.5',
                driversWithPrematureRead && index === 0
                  ? 'tw-items-start'
                  : 'tw-items-center',
              )}
            >
              {showLine && (
                <span
                  className={clx(
                    'tw-absolute tw-left-2 tw-top-7 tw-h-14 tw-border-l',
                    !finalizedAt ? 'tw-border-focus-100' : 'tw-border-success',
                    {
                      'tw-h-[76px]': driversWithPrematureRead,
                    },
                  )}
                />
              )}
              <span
                className={clx(
                  'tw-relative tw-z-10 tw-bg-content-primary',
                  step.status === 'upcoming' ? 'tw-py-0.5' : 'tw-py-1',
                  { 'tw-pt-2.5': driversWithPrematureRead && index === 0 },
                )}
              >
                <span
                  className={clx(
                    'tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-focus-100 tw-bg-content-neutral',
                    {
                      'tw-bg-focus-100': step.status === 'completed',
                      'tw-border-none tw-bg-[linear-gradient(0deg,#459A18_12.39%,#67D42D_87.61%)]':
                        finalizedAt,
                    },
                  )}
                >
                  <CheckIcon className="tw-text-content-neutral" />
                </span>
              </span>

              <div className={clx('tw-flex tw-min-h-10 tw-items-center')}>
                <div className="tw-text-sm tw-text-content-base">
                  {step.id === 1 ? (
                    <>
                      <p className="tw-mb-0">{t('dashboard.data-imported')}</p>
                      <p className="tw-mb-0 tw-font-medium">
                        {!finalizedAt
                          ? `${step.progressAmount}/${step.totalDrivers} ${t('dashboard.drivers-count')}`
                          : `${step.totalDrivers} ${t('dashboard.drivers-count')}`}
                      </p>
                      {!!driversWithPrematureRead && (
                        <>
                          <p className="tw-font-xs tw-mb-0 tw-mt-1 tw-text-error">
                            {t('dashboard.including')}{' '}
                            <span className="tw-font-medium">{`${driversWithPrematureRead} ${driversWithPrematureRead === 1 ? t('dashboard.driver-singular') : t('dashboard.drivers-count')}`}</span>
                          </p>{' '}
                          <p className="tw-font-xs tw-mb-0 tw-font-medium tw-text-error">
                            {t('dashboard.premature-read-date')}
                          </p>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {finalizedAt ? (
                        <>
                          <p className="tw-mb-0 tw-opacity-80">
                            {t('dashboard.settled')}{' '}
                            <span className="tw-font-medium">
                              {finalizedAt}
                            </span>
                          </p>
                          <p className="tw-mb-0 tw-text-[20px] tw-font-semibold tw-text-content-base">
                            <span>
                              {step.progressAmount}/{step.totalDrivers}
                            </span>{' '}
                            {t('dashboard.drivers-count')}
                          </p>
                        </>
                      ) : (
                        <p className="tw-mb-0 tw-opacity-80">
                          {t('dashboard.settlement-available-after')}{' '}
                          <span className="tw-font-medium">
                            {readyToCountDate}
                          </span>
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </li>
          </React.Fragment>
        )
      })}
    </ol>
  )
}
